/* eslint-disable max-lines */
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_ID, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, RouteReuseStrategy } from '@angular/router';
import { Observable, fromEvent, combineLatest, of, from } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';

import { environment as ENV } from '@environment';
import { isEmpty, isNullOrUndefined } from '@app/util/util';
import { AppModule } from '@app/app.module';
import { BrowserProperties } from '@app/modules/core/interfaces/browser-properties';
import {
  BROWSER_PROPERTIES,
  IS_BROWSER,
  IS_MOBILE,
  IS_MOBILE_SAFARI,
  VIEWPORT_RESIZE,
  IS_SERVER,
  IS_SPIDER,
  LOCAL_STORAGE,
  SESSION_STORAGE
} from '@app/modules/core/tokens/app.tokens';
import { mapParams } from '@app/browser/url-params.mapper';
import { LocalStorage } from '@app/modules/core/interfaces/local-storage';
import { SessionStorage } from '@app/modules/core/interfaces/session-storage';
import { ANONYMOUS_ID, AUTH_TOKEN, PATH } from '@app/modules/core/constants/auth.constants';
import { State } from '@app/store/state/state';
import {
  fetchMessageAction, loadPageDataAction, loadUserDataFromInitAction, removeTokenAction, restoreHistoryAction
} from '@app/store/actions/global.actions';
import { environment } from '@environment';
import { TranslocoBrowserModule } from '@app/browser/transloco-browser.module';
import { CustomReuseStrategy } from '@app/modules/core/services/custom-reuse-strategy';
import { RANDOM_ID } from '@app/constants/general.constants';
import { UserService } from '@app/modules/core/services/user.service';
import { StatsigExperimentsService } from '@statsig/services/statsig.service';
import { STATSIG_EXPERIMENTS } from '@statsig/statsig.config';
import { isActiveMobileInterface } from '@app/modules/core/mappers/mobile-interface.mapper';
import { authTokenSessionCookies, validateSessionCookies } from '@app/modules/core/util/redirect-login';
import { LoginType } from '@app/entities/login-type';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import { fetchStatsigExperimentsSuccess } from '@statsig/store/statsig.actions';
import { decrypt, getAllowedParams, getKeyParams, getTokenParams } from '@app/helpers/cypher.helper';
import { BrowserSupportWebp, ListOfBrowserName } from '@app/constants/browser.const';
import { ToastrTypes } from '@app/entities/toastr';

export function getLocalStorage(): LocalStorage {

  return {

    getItem(key: string): string {
      return localStorage.getItem(key);
    },

    setItem(key: string, value: string): void {
      localStorage.setItem(key, value);
    },

    removeItem(key: string): void {
      localStorage.removeItem(key);
    },

    has(key: string): boolean {
      return !isNullOrUndefined(localStorage.getItem(key));
    }
  };
}

export function getSessionStorage(): SessionStorage {

  return {

    getItem(key: string): string {
      return sessionStorage.getItem(key);
    },

    setItem(key: string, value: string): void {
      sessionStorage.setItem(key, value);
    },

    removeItem(key: string): void {
      sessionStorage.removeItem(key);
    },

    has(key: string): boolean {
      return !isNullOrUndefined(sessionStorage.getItem(key));
    }
  };
}

export function browserProperties(): BrowserProperties {
  const ua = navigator.userAgent;
  const qualify = (str: string) => {
    return String(str).replace(/([ -])(?!$)/g, '$1?');
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getName = (guesses: any[]) => {
    const _guess = guesses.find((guess) => {
      return RegExp(`\\b${guess.pattern || qualify(guess)}\\b`, 'i').exec(ua) && (guess.label || guess);
    });
    return (_guess?.label || _guess) ?? 'unknown';
  }
  const name = getName(ListOfBrowserName);

  return {
    browserName: name,
    supportWebp: BrowserSupportWebp.includes(name)
  };
}

function testRegex(regex: RegExp): boolean {
  return regex.test(navigator.userAgent.toLowerCase());
}

export function isMobile(): boolean {
  const isiPad = testRegex(/ipad/i);
  const isiPod = testRegex(/ipod/i);
  const isWebOS = testRegex(/webos/i);
  const isiPhone = testRegex(/iphone/i);
  const isAndroid = testRegex(/android/i);
  const isiDevice = testRegex(/ipad|iphone|ipod/i);
  const isWindowsPhone = testRegex(/windows phone/i);
  const isSamsungBrowser = testRegex(/SamsungBrowser/i);
  const isMobileSafariBrowser = testRegex(/mobile safari/i);
  return isAndroid || isiPad || isiPhone || isiPod || isiDevice
    || isWebOS || isWindowsPhone || isSamsungBrowser || isMobileSafariBrowser;
}

export function isMobileSafari(): boolean {
  const isiPad = testRegex(/ipad/i);
  const isiPod = testRegex(/ipod/i);
  const isiPhone = testRegex(/iphone/i);
  return (isiPad || isiPod || isiPhone);
}

export type ViewportSizeInfo = Readonly<{
  width: number;
  height: number;
  actionBarVisible: boolean;
}>;

export function viewportResize(): Observable<ViewportSizeInfo> {
  const getViewportSize = (): ViewportSizeInfo => {
    const windoWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const actionBarVisible = windowHeight < document.body.offsetHeight;
    return { width: windoWidth, height: windowHeight, actionBarVisible };
  };
  return fromEvent(window, 'resize').pipe(
    startWith(getViewportSize()),
    map(x => getViewportSize())
  );
}

export function onAppInit(
  document: Document,
  localStorage: LocalStorage,
  store: Store<State>,
  userService: UserService,
  splitService: StatsigExperimentsService,
  cookieService: CookieService,
  headerService: HeaderService,
  router: Router,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  isMobile: boolean,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  isMobileSafari: boolean,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  viewportResize: Function,
): () => Observable<Object> {
  const params = mapParams(document.documentURI);
  const statsigExperiments: string[] = Object.values(STATSIG_EXPERIMENTS);
  let tokenLocalStorage = null;

  if (isActiveMobileInterface()) {
    const dynamicToken = getTokenParams(params);
    const dynamicKey = getKeyParams(params);

    const userToken = dynamicToken?.length && dynamicKey?.length ? decrypt(dynamicToken, dynamicKey) : null;
    const isInvalidToken = isEmpty(userToken) || userToken === 'null' || userToken === 'undefined';

    const redirect = () => {
      let rhref = null;

      const href = window?.location?.href?.split?.('?')?.[0] ?? null;
      const allowedParams = getAllowedParams(params);

      rhref = allowedParams?.length && href ? `${href}?${allowedParams}` : href;

      if (!isNullOrUndefined(rhref)) {
        rhref = rhref.replace(environment.urlPath, '');
        router.navigateByUrl(rhref);
      } else {
        headerService.forceUserLogin(LoginType.Anywhere);
        store.dispatch(fetchMessageAction({ toastr: { message: 'REDIRECT ERROR', type: ToastrTypes.ERROR } }));
      }
    };

    if (!isInvalidToken) {
      tokenLocalStorage = userToken;
      redirect();
    } else {
      headerService.forceUserLogin(LoginType.Anywhere);
      store.dispatch(fetchMessageAction({ toastr: { message: 'ACCESS TOKEN WAS NOT PROVIDED', type: ToastrTypes.ERROR } }));
      redirect();
    }
  }

  if (typeof window !== 'undefined') {
    if (params[PATH]) {
      const url = window.location.href.slice(window.location.href.indexOf(PATH) + PATH.length + 1, window.location.href.length);
      router.navigateByUrl(decodeURIComponent(url));
    }
  }

  if (!isActiveMobileInterface() && validateSessionCookies(cookieService)) {
    tokenLocalStorage = authTokenSessionCookies(cookieService);
  }

  if (!isActiveMobileInterface() && isPreviousRouteLogin()) {
    store.dispatch(restoreHistoryAction());
  }

  const userInfo$ = userService.getUserInfo().pipe(
    take(1),
    catchError(() => of(null)),
    shareReplay(1)
  );

  const isAuthenticated$ = (environment.use_cookies_auth && !isActiveMobileInterface()) ?
    userInfo$.pipe(
      map(userInfo => !!userInfo),
      catchError(() => of(false))
    ) : of(!!tokenLocalStorage);

  return () => {
    return isAuthenticated$.pipe(
      switchMap(isAuthenticated => {
        if (isAuthenticated) {
          localStorage.setItem(AUTH_TOKEN, tokenLocalStorage);
          return combineLatest([userService.getData(), userInfo$]).pipe(
            switchMap(([user, userInfo]) => {
              const { application_user_id, has_rappicard, is_prime, loyalty_level, promo_code, rappi_segment } = userInfo;
              const experiments$ = from(splitService.initStatsig(
                user.id.toString(),
                statsigExperiments,
                {
                  application_user_id,
                  has_rappicard,
                  is_prime,
                  loyalty_level,
                  promo_code,
                  rappi_segment
                }
              ));
              return experiments$.pipe(
                take(1),
                map(experiments => ({ user, userInfo, experiments })),
                catchError(() => {
                  return of({ user, userInfo, experiments: [] })
                })
              );
            }),
            map(data => {
              store.dispatch(loadUserDataFromInitAction({ ...data }));
              return data;
            }),
            catchError((error) => {
              store.dispatch(removeTokenAction());
              headerService.forceUserLogin(LoginType.Anywhere);
              return error;
            })
          );
        } else {
          const anonymous_id = localStorage.getItem(ANONYMOUS_ID) ?? `ANONYMOUS_${Math.floor(Math.random() * RANDOM_ID)}`;
          localStorage.setItem(ANONYMOUS_ID, anonymous_id);
          splitService.initStatsig(anonymous_id, statsigExperiments);
          const experiments$ = from(splitService.initStatsig(
            anonymous_id,
            statsigExperiments,
          ));
          return experiments$.pipe(
            take(1),
            map(experiments => {
              store.dispatch(fetchStatsigExperimentsSuccess({ experiments }));
              store.dispatch(loadPageDataAction({ user: null, userInfo: null, experiments, isLoggedIn: false }));
              return experiments;
            }),
            catchError((error) => {
              store.dispatch(loadPageDataAction({ user: null, userInfo: null, experiments: [], isLoggedIn: false }));
              return error;
            })
          )
        }
      })
    );
  };
}

export function isPreviousRouteLogin(): boolean {
  return ENV.web_login.includes(document?.referrer);
}
@NgModule({
  imports: [
    AppModule,
    BrowserAnimationsModule,
    TranslocoBrowserModule
  ],
  exports: [AppModule],
  providers: [
    { provide: APP_ID, useValue: 'ng-rappi-travel' },
    { provide: IS_BROWSER, useValue: true },
    { provide: IS_SERVER, useValue: false },
    { provide: IS_MOBILE, useFactory: isMobile, deps: [] },
    { provide: IS_MOBILE_SAFARI, useFactory: isMobileSafari, deps: [] },
    { provide: VIEWPORT_RESIZE, useFactory: viewportResize, deps: [] },
    { provide: IS_SPIDER, useValue: false },
    { provide: LOCAL_STORAGE, useFactory: getLocalStorage, deps: [] },
    { provide: SESSION_STORAGE, useFactory: getSessionStorage, deps: [] },
    { provide: BROWSER_PROPERTIES, useFactory: browserProperties, deps: [] },
    provideAppInitializer(() => {
      const initializerFn = (onAppInit)(
        inject(DOCUMENT),
        inject(LOCAL_STORAGE) as unknown as LocalStorage,
        inject(Store),
        inject(UserService),
        inject(StatsigExperimentsService),
        inject(CookieService),
        inject(HeaderService),
        inject(Router),
        inject(IS_MOBILE) as unknown as boolean,
        inject(IS_MOBILE_SAFARI) as unknown as boolean,
        inject(VIEWPORT_RESIZE) as unknown as Function
      );
      return initializerFn();
    }),
    { provide: LOCALE_ID, useValue: environment.countryInfo.locale },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy }
  ]
})
export class AppBaseBrowserModule { }
